/**
 * React Query hooks for LPA Schedules.
 *
 * Schedules are audit-scoped (each schedule belongs to one audit).
 * Each schedule is a "layer" — e.g., "Operator daily", "Supervisor weekly" —
 * with frequency, auditor, date range, active flag, and assigned questions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  fetchSchedulesByAuditId,
  fetchScheduleById,
  fetchScheduleQuestions,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  syncScheduleQuestions,
} from '../api/schedules';
import type {
  CreateScheduleInput,
  UpdateScheduleInput,
  SyncScheduleQuestionsInput,
} from '../types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch all schedules for an audit with auditor profiles + question counts. */
export function useSchedulesByAuditId(auditId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.modLpaSchedules(auditId ?? null),
    queryFn: () => fetchSchedulesByAuditId(auditId!),
    enabled: !!auditId,
  });
}

/** Fetch a single schedule by ID with auditor profile + question counts. */
export function useScheduleById(scheduleId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.modLpaScheduleDetail(scheduleId ?? null),
    queryFn: () => fetchScheduleById(scheduleId!),
    enabled: !!scheduleId,
  });
}

/** Fetch assigned questions for a schedule (with question details + response guard). */
export function useScheduleQuestions(scheduleId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.modLpaScheduleQuestions(scheduleId ?? null),
    queryFn: () => fetchScheduleQuestions(scheduleId!),
    enabled: !!scheduleId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (
      input: Omit<CreateScheduleInput, 'tenant_id'>,
    ) =>
      createSchedule({
        ...input,
        tenant_id: selectedTenantId!,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaSchedules(variables.audit_id),
      });
      // Also refresh audit detail (schedule count changes)
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAuditDetail(variables.audit_id),
      });
      toast({
        title: t('mod_lpa.schedules.createSuccess', 'Schedule created'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.schedules.createError', 'Failed to create schedule'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      scheduleId,
      auditId,
      input,
    }: {
      scheduleId: string;
      auditId: string;
      input: UpdateScheduleInput;
    }) => updateSchedule(scheduleId, input),
    onSuccess: (_data, { scheduleId, auditId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaSchedules(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleDetail(scheduleId),
      });
      toast({
        title: t('mod_lpa.schedules.updateSuccess', 'Schedule updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.schedules.updateError', 'Failed to update schedule'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      scheduleId,
    }: {
      scheduleId: string;
      auditId: string;
    }) => deleteSchedule(scheduleId),
    onSuccess: (_data, { auditId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaSchedules(auditId),
      });
      // Refresh audit detail (schedule count changes)
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaAuditDetail(auditId),
      });
      toast({
        title: t('mod_lpa.schedules.deleteSuccess', 'Schedule deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.schedules.deleteError', 'Failed to delete schedule'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useSyncScheduleQuestions() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      scheduleId,
      assignments,
    }: {
      scheduleId: string;
      auditId: string;
      assignments: SyncScheduleQuestionsInput[];
    }) => syncScheduleQuestions(scheduleId, selectedTenantId!, assignments),
    onSuccess: (_data, { scheduleId, auditId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleQuestions(scheduleId),
      });
      // Question counts change — refresh schedule list + detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaSchedules(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleDetail(scheduleId),
      });
      toast({
        title: t('mod_lpa.schedules.syncSuccess', 'Questions updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.schedules.syncError', 'Failed to update questions'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
